import React, { Component, ReactElement } from 'react';
import { render } from 'react-dom';
import { COLOR } from 'const/color';
import { mapIconRoute } from '../oldPlanner/planner-map/map/PlannerMapMapUtils';
import { Location, middleOfTheWorld } from '../../types/orderTypes';
import { BundleRoute, BundleRouteStep } from '../../types/bundleTypes';
import './RoutesMap.scss';
import { bigMapMarker } from '../../components/maps/mapMarkers';
import { pbMapStyle, zoomMap } from '../../components/maps/mapUtils';
import { getBundleFontColor } from '../oldPlanner/utils/bundleDraft';
import { formatEta, formatWindow, getRouteColor } from './RouteUtils';

export declare interface RouteMarker {
  bundle: BundleRoute;
  routeStep: BundleRouteStep;
  step: number;
  group: string;
  color: string;
  selected: boolean;
  groupColor: string;
  isHub: boolean;
}

class MapMarkerContainer {
  id: string;
  routeMarkers: RouteMarker[];
  mapMarker: google.maps.Marker;

  constructor(id: string, orderMarkers: RouteMarker[]) {
    this.id = id;
    this.routeMarkers = orderMarkers;
  }

  getRouteStep(): BundleRouteStep {
    return this.routeMarkers[0].routeStep;
  }

  isMultiple(): boolean {
    return this.routeMarkers.length > 1;
  }

  getLabel(showMarkerIndex: boolean): string {
    if (this.routeMarkers[0].step === 0) {
      return 'P';
    } else if (this.isMultiple()) {
      return 'M';
    }
    return showMarkerIndex ? `${this.routeMarkers[0].step}` : 'D';
  }

  getColor(): string {
    return this.routeMarkers[0].color;
  }

  getOpacity(): number {
    return 1;
  }

  getGroupColor() {
    return this.routeMarkers[0].groupColor;
  }

  isHub(): boolean {
    return this.routeMarkers[0].isHub;
  }
}

interface State {
  expandedMarker: MapMarkerContainer;
}

declare interface Props {
  routes: BundleRoute[];
  hubPositions: Location[];
  deliveryAreaBoundary?: google.maps.LatLngLiteral[][];
  showMarkerIndex: boolean;
}

export default class RoutesMap extends Component<Props, State> {
  private mapContainer: HTMLDivElement;

  private mapInstance: google.maps.Map;
  private infoWindow: google.maps.InfoWindow;

  private markers: MapMarkerContainer[];
  private hubMarkers: google.maps.Marker[];

  public deliveryAreaBorder: google.maps.Polygon;

  constructor(props) {
    super(props);
    this.state = { expandedMarker: null };
    this.markers = [];
    this.hubMarkers = [];
  }

  componentDidMount() {
    this.mapInstance = new google.maps.Map(this.mapContainer, {
      zoom: 10,
      center: middleOfTheWorld,
      clickableIcons: false,
      disableDefaultUI: true,
      mapTypeControl: false,
      zoomControl: true,
      disableDoubleClickZoom: false,
      styles: pbMapStyle,
    });

    this.infoWindow = new google.maps.InfoWindow({
      disableAutoPan: true,
      maxWidth: 400,
    });

    google.maps.event.addListener(this.mapInstance, 'click', clickEvent => {
      this.infoWindow.close();
      this.setState({ expandedMarker: null });
    });
    google.maps.event.addListener(this.infoWindow, 'closeclick', () => {
      this.setState({ expandedMarker: null });
    });

    this.hubMarkers = this.generateHubMarkers();
  }

  async componentDidUpdate(prevProps: Props, prevState, snapshot) {
    if (
      this.props.routes.length !== prevProps.routes.length ||
      this.props.showMarkerIndex !== prevProps.showMarkerIndex
    ) {
      this.clearMarkers();
    }
    this.updateMarkers();
    if (this.state.expandedMarker) {
      const targetElement = document.createElement('div');
      render(
        this.infoWindowContentMultiple(this.state.expandedMarker),
        targetElement
      );
      this.infoWindow.setContent(targetElement);
    }
    this.resetAndCreateHubMarkers();

    // Only zoom if new update
    if (this.props.routes.length > prevProps.routes.length) {
      this.zoomToMarkers();
    }
    this.addBoundaryCoordinates();
  }

  infoWindowContentMultiple(markerContainer: MapMarkerContainer): ReactElement {
    return (
      <div className="routes-map-infowindow">
        <div className="routes-map-infowindow--line">
          <span className="routes-map-infowindow--bundle">Bundle</span>
          <span className="routes-map-infowindow--step">#</span>
          <span className="routes-map-infowindow--order">Order</span>
          <span className="routes-map-infowindow--window">Window</span>
          <span className="routes-map-infowindow--eta">ETA</span>
        </div>
        {markerContainer.routeMarkers
          .sort((marker1, marker2) => {
            const bundleId1 = parseInt(marker1.bundle.id);
            const bundleId2 = parseInt(marker2.bundle.id);
            return bundleId1 !== bundleId2
              ? bundleId1 - bundleId2
              : marker1.step - marker2.step;
          })
          .map(currentMarker => {
            const currentStep = currentMarker.routeStep;
            return (
              <div
                key={`order-${currentStep.orderId}-select`}
                className="routes-map-infowindow--line"
              >
                <span className="routes-map-infowindow--bundle">
                  <p
                    style={{
                      backgroundColor: currentMarker.color,
                      color: getBundleFontColor(currentMarker.bundle.id),
                    }}
                    className="routes-map-infowindow--bundle__value"
                  >
                    {`${currentMarker.bundle.id}`}
                  </p>
                </span>
                <span className="routes-map-infowindow--step">
                  {currentMarker.step}
                </span>
                <span className="routes-map-infowindow--order">
                  {currentStep.orderId}
                </span>
                <span className="routes-map-infowindow--window">
                  {formatWindow(currentStep)}
                </span>
                <span className="routes-map-infowindow--eta">
                  {formatEta(currentStep)}
                </span>
              </div>
            );
          })}
      </div>
    );
  }

  resetAndCreateHubMarkers() {
    this.hubMarkers.forEach(marker => marker.setMap(null));
    this.hubMarkers = [];
    this.generateHubMarkers().forEach(marker => this.hubMarkers.push(marker));
  }

  addBoundaryCoordinates() {
    if (this.deliveryAreaBorder) {
      this.deliveryAreaBorder.setMap(null);
    }
    if (this.props.deliveryAreaBoundary) {
      this.deliveryAreaBorder = new google.maps.Polygon({
        paths: this.props.deliveryAreaBoundary,
        strokeColor: '#096574',
        strokeOpacity: 0.8,
        strokeWeight: 1,
        fillOpacity: 0.1,
        clickable: false,
      });

      this.deliveryAreaBorder.setMap(this.mapInstance);
    }
  }

  zoomToMarkers() {
    const allMarkers = this.hubMarkers.concat(
      this.markers.map(marker => marker.mapMarker)
    );
    zoomMap(allMarkers, this.mapInstance);
  }

  clearMarkers = () => {
    this.markers.forEach(container => container.mapMarker.setMap(null));
    this.markers = [];
  };

  updateMarker = (
    existingMarkerContainer: MapMarkerContainer,
    newMarkerContainer: MapMarkerContainer
  ) => {
    existingMarkerContainer.routeMarkers = newMarkerContainer.routeMarkers;
  };

  markerKey = (bundleRouteStep: RouteMarker): string => {
    const routeStep = bundleRouteStep.routeStep;
    const isHub = this.props.hubPositions.some(
      hubPos =>
        hubPos.latitude === routeStep.latitude &&
        hubPos.longitude === routeStep.longitude
    );
    return isHub ? 'HUB' : `${routeStep.latitude}-${routeStep.longitude}`;
  };

  updateMarkers = () => {
    const hubPositions = this.props.hubPositions;
    const routeSteps: RouteMarker[] = this.props.routes.flatMap(route => {
      const bundleColor = getRouteColor(route.id);
      return route.steps.map((routeStep: BundleRouteStep, idx: number) => {
        return {
          routeStep: routeStep,
          group: `${routeStep.windowStart}-${routeStep.windowEnd}`,
          step: idx,
          color: bundleColor,
          selected: true,
          bundle: route,
          groupColor: '',
          isHub: hubPositions.some(
            hubPos =>
              hubPos.latitude === routeStep.latitude &&
              hubPos.longitude === routeStep.longitude
          ),
        };
      });
    });

    const markerLookup = this.markers.reduce(
      (map, multipleMarkersContainer) => {
        map.set(multipleMarkersContainer.id, multipleMarkersContainer);
        return map;
      },
      new Map<string, MapMarkerContainer>()
    );

    const generatedMarkers: {
      locationKey: string;
      routeMarker: RouteMarker;
    }[] = routeSteps
      .map(routeStep => {
        return {
          locationKey: this.markerKey(routeStep),
          routeMarker: routeStep,
        };
      })
      .flat();

    const groupedMarkers = generatedMarkers.reduce(
      (resultingMap, currentItem) => {
        const processedMarker = currentItem.routeMarker;
        if (resultingMap.has(currentItem.locationKey)) {
          resultingMap
            .get(currentItem.locationKey)
            .routeMarkers.push(processedMarker);
        } else {
          const mapMarker = new MapMarkerContainer(currentItem.locationKey, [
            processedMarker,
          ]);

          resultingMap.set(currentItem.locationKey, mapMarker);
        }
        return resultingMap;
      },
      new Map<string, MapMarkerContainer>()
    );

    const groupedMarkersList: MapMarkerContainer[] = Array.from(
      groupedMarkers.values()
    ).flat();

    const newAndUpdatedMarkers: MapMarkerContainer[] = groupedMarkersList
      .filter(markerContainer => !markerContainer.isHub())
      .map(multipleMarkerContainer => {
        const existingContainer = markerLookup.get(multipleMarkerContainer.id);
        if (existingContainer) {
          this.updateMarker(existingContainer, multipleMarkerContainer);
          return existingContainer;
        } else {
          multipleMarkerContainer.mapMarker = this.createMarker(
            multipleMarkerContainer
          );
          return multipleMarkerContainer;
        }
      });

    newAndUpdatedMarkers.forEach(multipleMarkerContainer => {
      multipleMarkerContainer.mapMarker.setVisible(true);
      multipleMarkerContainer.mapMarker.setMap(this.mapInstance);
    });

    this.markers = newAndUpdatedMarkers;
  };

  createMarkerOptsForContainer(
    mapContainer: MapMarkerContainer
  ): google.maps.MarkerOptions {
    return this.createMarkerOpts(
      mapContainer.getLabel(this.props.showMarkerIndex),
      mapContainer.getRouteStep(),
      mapContainer.getGroupColor(),
      mapContainer.getColor(),
      mapContainer.getOpacity()
    );
  }

  createMarkerOpts = (
    text: string,
    routeStep: BundleRouteStep,
    groupColor: string,
    color: string,
    opacity: number
  ) => {
    const lat = routeStep.latitude;
    const lng = routeStep.longitude;

    return {
      position: { lat, lng },
      icon: mapIconRoute(text, groupColor, color),
      title: `${routeStep.orderId}`,
      opacity,
    };
  };

  markerClickEvent = (
    markerContainer: MapMarkerContainer,
    marker: google.maps.Marker
  ) => {
    const targetElement = document.createElement('div');
    render(this.infoWindowContentMultiple(markerContainer), targetElement);
    this.infoWindow.setContent(targetElement);
    this.infoWindow.open(this.mapInstance, marker);
    this.setState({ expandedMarker: markerContainer });
  };

  createMarker = (markerContainer: MapMarkerContainer): google.maps.Marker => {
    const marker = new google.maps.Marker(
      this.createMarkerOptsForContainer(markerContainer)
    );
    marker.addListener('click', () =>
      this.markerClickEvent(markerContainer, marker)
    );
    return marker;
  };

  generateHubMarkers = (): google.maps.Marker[] => {
    return this.props.hubPositions.map(hub => {
      return new google.maps.Marker({
        position: {
          lat: hub.latitude,
          lng: hub.longitude,
        },
        icon: {
          url: bigMapMarker('HUB', COLOR.WHITE, 'black'),
          anchor: new google.maps.Point(20, 40),
          scaledSize: new google.maps.Size(40, 40),
          labelOrigin: new google.maps.Point(20, 20),
        },
        label: '',
        map: this.mapInstance,
      });
    });
  };

  render() {
    return (
      <div
        ref={node => {
          this.mapContainer = node;
        }}
        style={{ width: '80%' }}
      />
    );
  }
}
